/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  CompanyContainer,
  HeaderTitle,
  IconButton,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableWrapper,
  TwoColumns,
} from '../../../services/settings/appStyle/global.style';
import { Table, IconButton as IconButtonWithText, Loader } from '../../../Components/uielements';
import LogbookService from '../../../services/bioplus-services/logBook';
import DateUtils from '../../../utils/date-utils';
import { extractData, getDeclarationQuestions } from './logHelper';
import { ClosedEyeGray, DownloadIcon, EyeIcon } from '../../../Components/icons';
import { IntlMessages } from '../../../helper/langHelper';
import { exportExcelFile } from '../../../helper/exportExcelHelper';
import { useCompany } from '../../../hooks/CompanyContext';
import moment from 'moment';
import DocumentService from '../../../services/bioplus-services/document';
import { useIntl } from 'react-intl';
import DeclarationModal from './declarationModalView';
import { useUserContext } from '../../../hooks/UserContext';

const AdminLogRecords = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [startDate, setStartDate] = useState(DateUtils.last7Days);
  const [isDeclarationView, setIsDeclarationView] = useState(false);
  const [declarationData, setDeclarationData] = useState();
  const [isExportLoading, setIsExportLoading] = useState(false);

  const intl = useIntl();
  const alertMessagedeclarationFailed = intl.formatMessage({
    id: 'help.text.declaration.error',
  });

  const userContext = useUserContext();
  const companyContext = useCompany();
  const companyFilter = companyContext?.company?._id;
  const { userPermission } = userContext;
  const isUserAllowToManage = userPermission?.find((permission) => permission?.label === 'logbook');

  const canDownloadReport = !!isUserAllowToManage?.isReport;

  const query = useQuery(['logBookEntriesAdmin', page, companyFilter], () =>
    LogbookService.listByAdmin(page, pageSize, companyFilter)
  );

  const tableData = query?.data?.data ?? [];

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };

  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  //   table columns
  const userColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.visitor.name" />,
        accessor: 'userName',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.userId?.firstName} ${cellData?.row?.original?.userId?.lastName}`}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.company" />,
        accessor: 'companyId.name',
      },
      {
        Header: <IntlMessages id="table.col.premise" />,
        accessor: 'premiseId.propertyName',
      },
      {
        Header: <IntlMessages id="table.col.zone" />,
        accessor: 'geofenceId.name',
      },
      {
        Header: <IntlMessages id="table.col.service.role" />,
        accessor: 'serviceRole',
      },
      {
        Header: <IntlMessages id="table.col.entry.date" />,
        accessor: 'entryDate',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.entry.time" />,
        accessor: 'entryDate',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('h:mm:ss A') : '--'}
          </TableCell>
        ),
        id: 'entryTime',
      },
      {
        Header: <IntlMessages id="table.col.exit.time" />,
        accessor: 'exitDate',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('h:mm:ss A') : '--'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="text.declaration" />,
        accessor: 'declaration',
        isAction: true,
      },
    ],
    []
  );

  const handleDeclarationLink = async (data) => {
    try {
      const content = await DocumentService.getDocument(data?.declarationId);
      const declarationContent = {
        ...content,
        geofenceName: data?.geofenceName,
        premise: data?.premiseName,
      };
      if (content && declarationContent) {
        setDeclarationData(declarationContent);
        setIsDeclarationView(true);
      }
    } catch (error) {
      alert(alertMessagedeclarationFailed);
    }
  };

  const handleClosed = () => {
    setIsDeclarationView(false);
  };

  const actionList = [
    (data) => (
      <>
        {data?.declarationId ? (
          <IconButton onClick={() => handleDeclarationLink(data)}>
            <EyeIcon style={{ width: '18px', height: '18px' }} />
          </IconButton>
        ) : (
          <IconButton>
            <ClosedEyeGray style={{ width: '18px', height: '18px', color: 'red' }} />
          </IconButton>
        )}
      </>
    ),
  ];

  // Function to handle Excel export with all records
  const handleExcelExport = async () => {
    try {
      setIsExportLoading(true);

      // Fetch all records for report
      const reportData = await LogbookService.listByAdminForReport(companyFilter);

      // Extract the data array - handle both data.data and data structures
      const allRecords = reportData?.data ? reportData.data : reportData;

      if (allRecords && Array.isArray(allRecords)) {
        const excelFormatData = extractData(allRecords);
        const fileName = `logRecords_${moment().format('YYYY-MM-DD')}.xlsx`;
        const columnConfig = [
          { header: 'Visitor Name', key: 'VisitorName', width: 30 },
          { header: 'Entity Name', key: 'Company', width: 30 },
          { header: 'premise Name', key: 'premiseName', width: 30 },
          { header: 'Geofence Name', key: 'Geofence', width: 30 },
          { header: 'Service Role', key: 'serviceRole', width: 30 },
          { header: 'Date', key: 'Date', width: 20 },
          { header: 'Entry Time', key: 'entryTime', width: 20 },
          { header: 'Exit Time', key: 'exitTime', width: 20 },
        ];

        exportExcelFile(excelFormatData, fileName, columnConfig);
      }
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export records. Please try again.');
    } finally {
      setIsExportLoading(false);
    }
  };

  if (query.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <StyledContainer>
      <CompanyContainer>
        <HeaderTitle>
          <IntlMessages id="nav.records.logRecords" />
        </HeaderTitle>
        <TwoColumns style={{ gap: '10px' }}>
          {tableData && tableData.length > 0 && canDownloadReport && (
            <IconButtonWithText
              text={<IntlMessages id="button.text.downloadExcel" />}
              icon={<DownloadIcon />}
              onClick={handleExcelExport}
              disabled={isExportLoading}
            />
          )}
        </TwoColumns>
      </CompanyContainer>
      <TableWrapper>
        <Table
          data={tableData}
          columns={userColumns}
          hasFooter={true}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={query?.data?.total || 0}
          actions={actionList}
        />
      </TableWrapper>
      {declarationData && (
        <DeclarationModal
          open={isDeclarationView}
          onClose={handleClosed}
          userData={declarationData}
        />
      )}
    </StyledContainer>
  );
};

export default AdminLogRecords;
